// Currency conversion utilities for financial data processing
// Exchange rate as of December 2024: 1 EUR = 4.97 RON

export const RON_TO_EUR_RATE = 4.97;
export const EUR_TO_RON_RATE = 4.97;

/**
 * Convert amount from Romanian Lei (RON) to Euro (EUR)
 * @param ronAmount - Amount in RON
 * @returns Amount in EUR (rounded to 2 decimal places)
 */
export function convertRonToEur(ronAmount: number): number {
  if (!ronAmount || ronAmount <= 0) return 0;
  return Math.round((ronAmount / RON_TO_EUR_RATE) * 100) / 100;
}

/**
 * Convert amount from Euro (EUR) to Romanian Lei (RON)
 * @param eurAmount - Amount in EUR
 * @returns Amount in RON (rounded to 2 decimal places)
 */
export function convertEurToRon(eurAmount: number): number {
  if (!eurAmount || eurAmount <= 0) return 0;
  return Math.round(eurAmount * EUR_TO_RON_RATE * 100) / 100;
}

/**
 * Format currency amount with proper number formatting and currency symbol
 *
 * FORMATTING RULES:
 * - RON: Uses standard Romanian formatting with decimal places and comma separators
 * - EUR: Rounded to whole numbers (no decimals) with period separators for consistency
 *
 * @param amount - Amount to format
 * @param currency - Currency code ('RON' or 'EUR')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: "RON" | "EUR"
): string {
  if (!amount) return `0 ${currency}`;

  if (currency === "EUR") {
    // Round EUR to whole numbers and format without decimals
    const roundedAmount = Math.round(amount);
    const formatted = new Intl.NumberFormat("ro-RO", {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(roundedAmount);
    return `${formatted} ${currency}`;
  }

  // Standard formatting for RON
  const formatted = new Intl.NumberFormat("ro-RO").format(amount);
  return `${formatted} ${currency}`;
}

/**
 * Format amount showing both RON and EUR values
 * @param ronAmount - Original amount in RON
 * @returns Formatted string showing both currencies
 */
export function formatDualCurrency(ronAmount: number): string {
  if (!ronAmount) return "0 RON (0 EUR)";

  const eurAmount = convertRonToEur(ronAmount);
  return `${formatCurrency(ronAmount, "RON")} (${formatCurrency(eurAmount, "EUR")})`;
}

/**
 * Get the current exchange rate information for display
 * @returns Exchange rate information object
 */
export function getExchangeRateInfo() {
  return {
    rate: RON_TO_EUR_RATE,
    date: "2024-12-31",
    description: `Rata de schimb: 1 EUR = ${RON_TO_EUR_RATE} RON (decembrie 2024)`,
  };
}
